import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Plus, Edit2, Trash2, MessageCircle, Users as UsersIcon, Hash } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button, Card, Spinner, EmptyState, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Textarea, DeleteConfirmModal,
} from '../../../components/ui'
import { Thead, Tbody, Tr, Th, Td } from '../../../components/ui/Table'

interface Channel {
  id: number; name: string; slug: string; color: string | null; description: string | null
  sort_order: number; is_active: boolean; members_count: number; messages_count: number
}

export default function CommunityChannelsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [channels, setChannels] = useState<Channel[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteChannel, setDeleteChannel] = useState<Channel | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [form, setForm] = useState({ name: '', color: '#5865F2', description: '' })

  useEffect(() => { fetchChannels() }, [])

  async function fetchChannels() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl('/v1/community/channels'))
      setChannels(res.data.data)
    } catch {} finally { setLoading(false) }
  }

  function openCreate() {
    setEditingChannel(null)
    setForm({ name: '', color: '#5865F2', description: '' })
    setModalOpen(true)
  }

  function openEdit(ch: Channel) {
    setEditingChannel(ch)
    setForm({ name: ch.name, color: ch.color || '#5865F2', description: ch.description || '' })
    setModalOpen(true)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) return
    setSaving(true)
    try {
      if (editingChannel) {
        await axios.put(buildApiUrl(`/v1/community/channels/${editingChannel.id}`), form)
        toast('success', 'Canal atualizado')
      } else {
        await axios.post(buildApiUrl('/v1/community/channels'), form)
        toast('success', 'Canal criado')
      }
      setModalOpen(false)
      fetchChannels()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro')
    } finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!deleteChannel) return
    setDeleting(true)
    try {
      await axios.delete(buildApiUrl(`/v1/community/channels/${deleteChannel.id}`))
      toast('success', 'Canal removido')
      setDeleteChannel(null)
      fetchChannels()
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro')
    } finally { setDeleting(false) }
  }

  if (loading) return <div className="flex items-center justify-center py-16"><Spinner /></div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Canais</h1>
            <p className="text-gray-500 mt-1">Canais de discussão da comunidade</p>
          </div>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Canal
        </Button>
      </div>

      {channels.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageCircle className="w-8 h-8" />}
            title="Nenhum canal"
            description="Crie canais pra os membros da comunidade conversarem."
            action={<Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />Criar canal</Button>}
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <Thead>
                <Tr>
                  <Th>Canal</Th>
                  <Th>Inscritos</Th>
                  <Th>Mensagens</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {channels.map(ch => (
                  <Tr key={ch.id} onClick={() => navigate(`/community/channels/${ch.id}`)} className="cursor-pointer hover:bg-gray-50">
                    <Td>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
                          style={{ backgroundColor: ch.color || '#5865F2' }}
                        >
                          {ch.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-1">
                            <Hash className="w-3 h-3 text-gray-400" />
                            <p className="text-sm font-medium text-gray-800">{ch.name}</p>
                          </div>
                          {ch.description && <p className="text-xs text-gray-400 truncate max-w-[200px]">{ch.description}</p>}
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <UsersIcon className="w-3.5 h-3.5 text-gray-400" />
                        {ch.members_count}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <MessageCircle className="w-3.5 h-3.5 text-gray-400" />
                        {ch.messages_count}
                      </span>
                    </Td>
                    <Td>
                      <Badge variant={ch.is_active ? 'success' : 'gray'} size="sm">
                        {ch.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); openEdit(ch) }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setDeleteChannel(ch) }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-error-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal criar/editar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setModalOpen(false)}>
          {editingChannel ? 'Editar canal' : 'Novo canal'}
        </ModalHeader>
        <form onSubmit={handleSave}>
          <ModalBody className="space-y-4">
            <Input label="Nome *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Saúde" required />
            <Input label="Cor" type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} />
            <Textarea label="Descrição" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Sobre o que é este canal" rows={2} />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>{editingChannel ? 'Salvar' : 'Criar'}</Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Confirmar exclusão */}
      <DeleteConfirmModal
        open={!!deleteChannel}
        onClose={() => setDeleteChannel(null)}
        onConfirm={handleDelete}
        title="Excluir canal"
        message={`Tem certeza que deseja excluir o canal "${deleteChannel?.name}"? Todas as mensagens serão perdidas.`}
      />
    </div>
  )
}
